/* eslint-disable no-fallthrough */
import snakeCase from 'lodash/snakeCase';
import {
  all,
  call,
  put,
  select,
  race,
  delay,
  takeEvery,
} from 'redux-saga/effects';
import { getEnabledFlagsList } from '@wix/da-react-context/pkg/flags/redux/selectors';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import i18next from 'i18next';
import ddt from '@wix/da-ddt';

import {
  updateModule,
  fetchModuleEndpoint,
  fetchModuleEndpointSuccess,
  fetchModuleEndpointError,
  /* We will be sunsetting the actions bellow in favor of fetchModuleEndpoint* */
  fetchModuleConfig,
  fetchModuleConfigSuccess,
  fetchModuleConfigError,
  saveModuleConfig,
  saveModuleConfigSuccess,
  saveModuleConfigError,
} from '../actions/modules';
import {
  getModuleType,
  getModuleConfig,
  getModuleVersion,
} from '../selectors/modules';
import { getProfileGruser, getProfileOwnerUser } from '../selectors/users';
import { getModuleInitActions } from '../store/initActions/modules';
import { getConfigKey } from '@wix/da-redux-config/pkg/selectors';
import { requestNapi, isApiError } from '@wix/da-http-client';
import { API_BASE_PATH } from '../../constants';
import { requestPuppyGruser } from './gruserHelpers';
import { Gruser } from '@wix/da-gruser-shared/pkg/types/papi';

// These are endpoints that write to draftJS they have to go via node
const ENDPOINTS_THAT_NEED_NODE = [
  'about/save',
  'group_about/save',
  'papi_custombox/save',
  'donations/save',
  'papigroupabout/save',
  'megabox/save',
];

function* postModuleEndpoint(endpoint, params) {
  if (ENDPOINTS_THAT_NEED_NODE.includes(endpoint)) {
    const gruser: Gruser = yield select(getProfileGruser);
    return yield call(requestNapi, {
      method: 'post',
      baseURL: API_BASE_PATH,
      url: `/module/${endpoint}`,
      data: {
        gruserid: gruser.gruserId,
        gruser_typeid: gruser.gruserTypeid,
        ...transformParamNames(params),
      },
    });
  }

  return yield call(
    requestPuppyGruser,
    {
      method: 'post',
      url: `/module/${endpoint}`,
      data: transformParamNames(params),
      enableCancellation: true,
    },
    undefined,
    'gruser'
  );
}

function* getModuleEndpoint(endpoint, params) {
  return yield call(
    requestPuppyGruser,
    {
      method: 'get',
      url: `/module/${endpoint}`,
      params: transformParamNames(params),
      enableCancellation: true,
    },
    undefined,
    'gruser'
  );
}

const isModuleResponse = response =>
  response &&
  response.hasOwnProperty('id') &&
  response.hasOwnProperty('version') &&
  response.hasOwnProperty('moduleData');

export function* runModuleInitActions(moduleResponse, requestParams = {}) {
  // Duck typing
  if (!isModuleResponse(moduleResponse)) {
    return;
  }

  const flags = yield select(getEnabledFlagsList);
  const actions = getModuleInitActions(moduleResponse, requestParams, flags);
  if (!actions || !actions.length) {
    // No module actions found
    return;
  }
  // dispatch actions
  yield all(actions.map(moduleAction => put(moduleAction)));
}

function transformParamNames(params) {
  return Object.keys(params).reduce((transformedParams, key) => {
    switch (key) {
      case 'moduleId':
      case 'folderId':
      case 'countryId':
        // convert to lovercase
        transformedParams[key.toLowerCase()] = params[key];
        break;
      case 'sourceType':
      case 'viewMode':
      case 'slideImageFit':
      case 'sortBy':
      // members:
      case 'rolesFilter':
      // active_members
      case 'displayLimit':
      // custombox
      case 'backgroundType':
      case 'imageDisplayMode':
      case 'imageDeviationid':
      case 'imageTint':
      case 'imageUrl':
      // about module
      case 'dobDay':
      case 'dobMonth':
      case 'dobYear':
      case 'websiteLabel':
      case 'showBadges':
      case 'socialLinks':
      case 'isArtist':
        // convert to snake_case
        transformedParams[snakeCase(key)] = params[key];
        break;
      default:
        transformedParams[key] = params[key];
    }
    return transformedParams;
  }, {});
}

export function* saveModuleConfigSaga(action) {
  const {
    payload: { _endpoint, ...payload },
  } = action;
  const { moduleId } = payload;
  const requestParams = payload;
  yield put(
    updateModule({ id: moduleId, moduleMeta: { isSavingConfig: true } })
  );

  const { username } = yield select(getProfileOwnerUser);
  const moduleType = yield select(getModuleType, moduleId);
  const { config = {} } = yield select(getModuleConfig, moduleId);
  const { version = payload.version } = config;
  const csrf = yield select(getConfigKey, 'csrfToken');

  const response = yield call(
    postModuleEndpoint,
    _endpoint || `${moduleType}/save`,
    {
      ...payload,
      username,
      version,
      csrf_token: csrf,
    }
  );
  yield put(
    updateModule({ id: moduleId, moduleMeta: { isSavingConfig: false } })
  );

  // Basic error handling
  if (!response || response.error) {
    const errorDescription = response?.errorDescription || 'Unknown error';
    ddt.log(`modules`, `save config error`, `${errorDescription}`);
    yield putErrorNotification(
      i18next.t('widgets.common.error.config.saveFailed')
    );
    yield put(
      saveModuleConfigError({
        id: moduleId,
        errorDescription,
      })
    );
    return;
  }

  if (response) {
    yield runModuleInitActions(response, requestParams);
    yield put(saveModuleConfigSuccess({ id: moduleId }));
  }
}

export function* fetchModuleConfigSaga(action) {
  const { payload } = action;
  const { moduleId } = payload;
  const moduleType = yield select(getModuleType, moduleId);
  const { username } = yield select(getProfileOwnerUser);

  const csrf = yield select(getConfigKey, 'csrfToken');

  yield put(
    updateModule({ id: moduleId, moduleMeta: { isFetchingConfig: true } })
  );

  const response = yield call(getModuleEndpoint, `${moduleType}/config`, {
    ...payload,
    csrf_token: csrf,
    username,
  });

  if (!response || response.error) {
    const errorDescription = response?.errorDescription || 'Unknown error';
    ddt.log(`modules`, `fetch config error`, `${errorDescription}`);
    yield putErrorNotification(
      i18next.t('widgets.common.error.config.fetchFailed')
    );
    yield put(
      fetchModuleConfigError({
        id: moduleId,
        errorDescription,
      })
    );
    return;
  }
  // update module data in the store
  yield put(fetchModuleConfigSuccess({ id: moduleId, config: response }));

  yield put(
    updateModule({ id: moduleId, moduleMeta: { isFetchingConfig: false } })
  );
}

export function* fetchModuleEndpointSaga(action) {
  const { payload } = action;
  const { id, path, method, ...data } = payload;
  const requestParams = payload;

  const version = yield select(getModuleVersion, id);
  const { username } = yield select(getProfileOwnerUser);
  const moduleType = yield select(getModuleType, id);
  const csrf = yield select(getConfigKey, 'csrfToken');

  const fetchEndpoint =
    method === 'get' ? getModuleEndpoint : postModuleEndpoint;

  const { response } = yield race({
    response: call(fetchEndpoint, `${moduleType}${path}`, {
      ...data,
      csrf_token: csrf,
      username,
      version,
      moduleId: id,
    }),
    timeout: delay(30 * 1000),
  });

  if (!response || isApiError(response)) {
    const errorDescription =
      response?.errorDetails?.Form ||
      response?.details ||
      response?.errorDescription ||
      i18next.t('widgets.common.error.endpoint.fetchFailed');

    ddt.log(`modules`, `fetch action error`, `${errorDescription}`);

    yield putErrorNotification(errorDescription);

    yield put(
      fetchModuleEndpointError({
        id,
        path,
        errorDescription,
      })
    );
    yield put(updateModule({ id, moduleMeta: { isFetching: false } }));
    return;
  }

  if (isModuleResponse(response)) {
    // update module data in the store
    yield runModuleInitActions(response, requestParams);
  } else {
    // endpoint does not return a module response, hence, no module version gets updated
    // module version is used as memoization key for many hooks, so if no version
    ddt.log(`modules`, `save endpoint does not return a module response`, {
      id,
      path,
      response,
    });
  }
  yield put(fetchModuleEndpointSuccess({ id, response, path }));
}

export default function* rootModulesSaga() {
  yield all([
    takeEvery(saveModuleConfig, saveModuleConfigSaga),
    takeEvery(fetchModuleConfig, fetchModuleConfigSaga),
    takeEvery(fetchModuleEndpoint, fetchModuleEndpointSaga),
  ]);
}
